import { supabase } from "@/lib/supabase";
import { testimonials as mockTestimonials } from "@/mocks/testimonials";

/* ---------------------------------- FAQs ----------------------------------- */

export interface PublicFaqRow {
  id: number;
  question: string;
  answer: string;
  category: string | null;
  scope: string | null;
  sort_order: number;
}

let faqCache: PublicFaqRow[] | null = null;
let faqPending: Promise<PublicFaqRow[]> | null = null;
const faqListeners = new Set<(rows: PublicFaqRow[]) => void>();

export function getPublicFaqs(): PublicFaqRow[] {
  return faqCache || [];
}

export function subscribePublicFaqs(listener: (rows: PublicFaqRow[]) => void): () => void {
  faqListeners.add(listener);
  return () => faqListeners.delete(listener);
}

export function loadPublicFaqs(): Promise<PublicFaqRow[]> {
  if (faqCache) return Promise.resolve(faqCache);
  if (faqPending) return faqPending;
  faqPending = (async () => {
    try {
      const { data, error } = await supabase
        .from("faqs")
        .select("id, question, answer, category, scope, sort_order")
        .eq("status", "published")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      const rows = (data as PublicFaqRow[] | null) || [];
      faqCache = rows;
      faqListeners.forEach((listener) => listener(rows));
      return rows;
    } catch {
      faqCache = [];
      return [];
    }
  })();
  return faqPending;
}

/* ------------------------------ Testimonials ------------------------------- */

export interface PublicTestimonial {
  name: string;
  role: string;
  location: string;
  quote: string;
  experience: string;
  experienceSlug: string;
  rating: number;
  avatar: string;
}

interface TestimonialRow {
  name: string;
  role: string | null;
  location: string | null;
  quote: string;
  experience: string | null;
  experience_slug: string | null;
  rating: number | null;
  avatar: string | null;
}

function mapTestimonialRow(row: TestimonialRow): PublicTestimonial {
  return {
    name: row.name,
    role: row.role || "",
    location: row.location || "",
    quote: row.quote || "",
    experience: row.experience || "",
    experienceSlug: row.experience_slug || "",
    rating: row.rating ?? 5,
    avatar: row.avatar || "",
  };
}

function mockTestimonialList(): PublicTestimonial[] {
  return mockTestimonials.map((item) => ({
    name: item.name,
    role: item.role,
    location: item.location,
    quote: item.quote,
    experience: item.experience,
    experienceSlug: item.experienceSlug,
    rating: item.rating,
    avatar: item.avatar,
  }));
}

export async function fetchPublicTestimonials(): Promise<PublicTestimonial[]> {
  try {
    const { data, error } = await supabase
      .from("testimonials")
      .select("name, role, location, quote, experience, experience_slug, rating, avatar")
      .eq("status", "published")
      .order("sort_order", { ascending: true });
    if (error) throw error;

    const rows = (data as TestimonialRow[] | null) || [];
    if (rows.length === 0) return mockTestimonialList();
    return rows.map(mapTestimonialRow);
  } catch {
    return mockTestimonialList();
  }
}

/* ----------------------------------- SEO ------------------------------------ */

export interface PublicSeo {
  title: string | null;
  description: string | null;
  keywords: string | null;
  image: string | null;
  /** Admin-configured canonical URL. Optional so older callers stay valid. */
  canonical?: string | null;
}

const seoCache = new Map<string, PublicSeo | null>();
const seoListeners = new Map<string, Set<(seo: PublicSeo | null) => void>>();

function notifySeo(path: string, seo: PublicSeo | null): void {
  seoListeners.get(path)?.forEach((listener) => listener(seo));
}

/** The last known SEO override for a path (null when none is published). */
export function getPublicSeo(path: string): PublicSeo | null {
  return seoCache.get(path) ?? null;
}

/** Subscribe to live updates for a single path (e.g. after an admin edit). */
export function subscribePublicSeo(
  path: string,
  listener: (seo: PublicSeo | null) => void,
): () => void {
  let set = seoListeners.get(path);
  if (!set) {
    set = new Set();
    seoListeners.set(path, set);
  }
  set.add(listener);
  return () => {
    set?.delete(listener);
    if (set && set.size === 0) seoListeners.delete(path);
  };
}

/**
 * Section wildcard for a concrete path, e.g. "/experiences/boats" -> "/experiences/*".
 * Lets one SEO entry cover every dynamic detail page under a section.
 */
export function wildcardPathFor(path: string): string | null {
  const parts = path.split("/").filter(Boolean);
  if (parts.length < 2) return null;
  return `/${parts[0]}/*`;
}

/** Used by the admin SEO module after saving so the live site picks up changes immediately. */
export function primeSeoOverride(path: string, seo: PublicSeo | null): void {
  seoCache.set(path, seo);
  notifySeo(path, seo);
  // A saved section entry (…/*) must also update any live detail pages that
  // resolved their override from it.
  if (path.endsWith("/*")) {
    seoListeners.forEach((listeners, key) => {
      if (key !== path && wildcardPathFor(key) === path) {
        listeners.forEach((listener) => listener(seo));
      }
    });
  }
}

/** Reads a page's SEO overrides from the backend, cached per path. */
export async function fetchPublicSeo(path: string): Promise<PublicSeo | null> {
  if (seoCache.has(path)) return seoCache.get(path) ?? null;

  // Try the exact path first, then fall back to the section wildcard entry.
  const candidates = [path];
  const wildcard = wildcardPathFor(path);
  if (wildcard) candidates.push(wildcard);

  for (const candidate of candidates) {
    try {
      const { data, error } = await supabase
        .from("seo_settings")
        .select("title, description, keywords, og_image, canonical")
        .eq("path", candidate)
        .eq("status", "published")
        .maybeSingle();
      if (error) throw error;
      if (data) {
        const row = data as {
          title: string | null;
          description: string | null;
          keywords: string | null;
          og_image: string | null;
          canonical: string | null;
        };
        const result: PublicSeo = {
          title: row.title || null,
          description: row.description || null,
          keywords: row.keywords || null,
          image: row.og_image || null,
          canonical: row.canonical || null,
        };
        seoCache.set(path, result);
        notifySeo(path, result);
        return result;
      }
    } catch {
      // Try the next candidate (exact -> wildcard).
    }
  }

  seoCache.set(path, null);
  notifySeo(path, null);
  return null;
}